/**
 * A static, non-interactive preview of the dashboard layout.
 * Rendered behind the auth card with blur to give users a glimpse
 * of the app they're signing into. Uses no live data or API calls.
 */
export function DashboardPreviewShell() {
  return (
    <div
      className="auth-dashboard-preview"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        overflow: "hidden",
        pointerEvents: "none",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 260,
          minWidth: 260,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "var(--color-auth-panel)",
          borderRight: "1px solid var(--color-auth-border)",
          padding: "24px 16px",
          gap: 8,
        }}
      >
        {/* Logo + badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: "linear-gradient(135deg, #8181FF 0%, #FD936E 100%)",
              flexShrink: 0,
            }}
          />
          <div
            style={{
              padding: "2px 8px",
              borderRadius: 12,
              background: "linear-gradient(90deg, #FD936E 0%, #8181FF 100%)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            Free
          </div>
        </div>

        {/* Nav items */}
        {["Projects", "Assets", "Messages"].map((label, i) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "10px 14px",
              borderRadius: 10,
              background: i === 0 ? "linear-gradient(90deg, #8181FF 0%, #FD936E 100%)" : "transparent",
              color: i === 0 ? "#fff" : "var(--color-auth-text-faint)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: 4,
                background: i === 0 ? "rgba(255,255,255,0.3)" : "var(--color-auth-border)",
                flexShrink: 0,
              }}
            />
            {label}
          </div>
        ))}

        {/* Manuals section */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 18px",
              color: "var(--color-auth-text-faint)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <div style={{ width: 20, height: 20, borderRadius: 4, background: "var(--color-auth-border)" }} />
            Manuals
          </div>
        </div>

        {/* Footer items */}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 4, borderTop: "1px solid var(--color-auth-border)", paddingTop: 16 }}>
          {["Settings", "Log out"].map((label) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "10px 14px",
                color: "var(--color-auth-text-faint)",
                fontSize: 14,
              }}
            >
              <div style={{ width: 18, height: 18, borderRadius: 4, background: "var(--color-auth-border)" }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Right column */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 40px",
            borderBottom: "1px solid var(--color-auth-border)",
            background: "var(--color-auth-panel)",
            minHeight: 72,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--color-auth-border)" }} />
            <div style={{ width: 200, height: 36, borderRadius: 8, background: "var(--color-auth-border)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--color-auth-border)" }} />
            <div style={{ width: 80, height: 24, borderRadius: 4, background: "var(--color-auth-border)" }} />
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--color-auth-border)" }} />
          </div>
        </div>

        {/* Content area */}
        <div style={{ flex: 1, padding: "32px 40px", overflow: "hidden" }}>
          {/* Page header */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ width: 120, height: 10, borderRadius: 4, background: "var(--color-auth-border)", marginBottom: 10 }} />
            <div style={{ width: 180, height: 28, borderRadius: 6, background: "var(--color-auth-text-faint)", opacity: 0.3, marginBottom: 8 }} />
            <div style={{ width: 300, height: 12, borderRadius: 4, background: "var(--color-auth-border)" }} />
          </div>

          {/* Filter chips */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, marginTop: 24 }}>
            {[100, 120, 110].map((w, i) => (
              <div key={i} style={{ width: w, height: 36, borderRadius: 8, border: "1px solid var(--color-auth-border)", background: "var(--color-auth-panel)" }} />
            ))}
          </div>

          {/* KPI cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 32 }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  padding: 20,
                  borderRadius: 16,
                  border: "1px solid var(--color-auth-border)",
                  background: "var(--color-auth-panel)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "linear-gradient(135deg, rgba(253,147,110,0.1), rgba(129,129,255,0.1))" }} />
                <div style={{ width: 60, height: 8, borderRadius: 4, background: "var(--color-auth-border)" }} />
                <div style={{ width: 40, height: 24, borderRadius: 4, background: "var(--color-auth-text-faint)", opacity: 0.25 }} />
              </div>
            ))}
          </div>

          {/* Project rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  border: "1px solid var(--color-auth-border)",
                  background: "var(--color-auth-panel)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ width: 160 + i * 30, height: 12, borderRadius: 4, background: "var(--color-auth-text-faint)", opacity: 0.3 }} />
                  <div style={{ width: 200, height: 8, borderRadius: 4, background: "var(--color-auth-border)" }} />
                </div>
                <div style={{ width: 70, height: 28, borderRadius: 999, border: "1px solid var(--color-auth-border)" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
